import React, { memo } from "react";

import { InfoCircleFill } from "@hl/base-components/lib/assets/icons.generated";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { Link } from "react-router-dom";

import {
  BlockchainEntity,
  BlockchainEntityStatus,
} from "components/BlockchainEntityTitle/types";
import { TOOLS_URL } from "config";
import { longFormatDate } from "utils/date";

import { useArchiveCollection } from "./collectionArchiveHook";

const CollectionArchiveModal = ({
  entity,
  isOpened,
  onClose,
}: {
  entity: BlockchainEntity;
  isOpened: boolean;
  onClose: () => void;
}) => {
  const theme = useMantineTheme();
  const { toggleArchive, loading } = useArchiveCollection({
    entity,
    onSuccess: onClose,
  });

  const isLive = entity.status === BlockchainEntityStatus.MINT_STARTED;
  const infoColor = theme.fn.themeColor(STATUS_COLOR.INFORMATIVE);

  return (
    <Modal
      opened={isOpened}
      onClose={onClose}
      size="lg"
      title="Archive collection"
    >
      <Stack spacing="xl">
        {isLive ? (
          <>
            <Text size="sm">
              Archiving this collection moves it to your archive. Collectors
              will have access to the mint page. To temporarily suspend minting,
              go to the{" "}
              <Link
                css={{ color: "inherit" }}
                to={TOOLS_URL.collections.manage.replace(
                  ":collectionId",
                  entity.id
                )}
              >
                collection management page
              </Link>{" "}
              to adjust the start and end dates. To reverse this action,
              unarchive the collection.
            </Text>
            <Card
              bg={STATUS_COLOR.INFORMATIVE_WASH}
              withBorder
              p="md"
              sx={{
                borderColor: infoColor,
              }}
            >
              <Group spacing={8} align="center">
                <InfoCircleFill color={infoColor} />
                <Text size="xs">
                  This collection is currently live
                  {entity.end
                    ? ` and will end on
                  ${longFormatDate(new Date(entity.end))}`
                    : ""}
                  .
                </Text>
              </Group>
            </Card>
          </>
        ) : (
          <Text size="sm">
            Archiving this collection will move it to your archive. Collectors
            will have access to the mint page. To reverse this action, unarchive
            the collection.
          </Text>
        )}
      </Stack>
      <ModalSection>
        <Group spacing={16} grow>
          <Button size="lg" color={SECONDARY_COLOR} onClick={onClose}>
            Cancel
          </Button>
          <Button size="lg" loading={loading} onClick={toggleArchive}>
            Archive
          </Button>
        </Group>
      </ModalSection>
    </Modal>
  );
};

export default memo(CollectionArchiveModal);
